import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, RotateCcw } from "lucide-react";

export interface WhatsAppCloudTemplateRow {
  id: string;
  template_id: string;
  name: string;
  language: string;
  category: string | null;
  status: string;
  rejected_reason: string | null;
  last_synced_at: string;
  components: unknown;
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  APPROVED: "default",
  PENDING: "secondary",
  REJECTED: "destructive",
  PAUSED: "outline",
  DISABLED: "destructive",
};

function formatSyncedAt(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return value;
  }
}

interface Props {
  templates: WhatsAppCloudTemplateRow[];
  loading: boolean;
  resubmitting: boolean;
  resubmittingId: string | null;
  onResubmit: (row: WhatsAppCloudTemplateRow) => void;
}

export function WhatsAppCloudTemplatesTable({
  templates,
  loading,
  resubmitting,
  resubmittingId,
  onResubmit,
}: Props) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Idioma</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Motivo rejeição</TableHead>
            <TableHead>Última sync</TableHead>
            <TableHead className="w-[100px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </TableCell>
            </TableRow>
          ) : templates.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                Nenhum template. Clique em Sincronizar para importar da Meta.
              </TableCell>
            </TableRow>
          ) : (
            templates.map((row) => {
              const statusKey = row.status?.toUpperCase() ?? "";
              return (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{row.language}</TableCell>
                <TableCell>{row.category || "—"}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[statusKey] || "outline"}>{statusKey || row.status}</Badge>
                </TableCell>
                <TableCell
                  className="max-w-[200px] truncate text-xs text-muted-foreground"
                  title={row.rejected_reason || undefined}
                >
                  {row.rejected_reason || "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatSyncedAt(row.last_synced_at)}
                </TableCell>
                <TableCell>
                  {statusKey === "REJECTED" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={resubmitting || resubmittingId === row.id}
                      onClick={() => onResubmit(row)}
                    >
                      {resubmittingId === row.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="mr-1 h-3 w-3" />
                          Reenviar
                        </>
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
